import { Box, Text } from '@mantine/core';
import { type FC, useEffect, useRef } from 'react';
import AgentMessageBubble from '@components/agent-shell/AgentMessageBubble';
import type { QuickHelpMessage } from '@providers/QuickHelp/types';

interface Props {
    messages: QuickHelpMessage[];
    statusText?: string;
}

const QuickHelpMessages: FC<Props> = ({ messages, statusText }) => {
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length, messages[messages.length - 1]?.content]);

    if (messages.length === 0) {
        return (
            <Box
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    gap: 8,
                }}
            >
                <Text size="sm" c="dimmed" ta="center">
                    Ask anything about your inventory, sales, forecasts, or suppliers.
                </Text>
                <Text size="xs" c="dimmed" ta="center">
                    You can also select any widget on the page and click "Add to Chat" to give me context.
                </Text>
            </Box>
        );
    }

    return (
        <Box
            style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: 10,
                paddingBottom: 8,
            }}
        >
            {messages.map((msg, i) => {
                const prev = messages[i - 1];
                const next = messages[i + 1];
                // Show anchor on the first assistant message of each turn
                const showAnchor =
                    msg.role === 'assistant' &&
                    (!prev || prev.role === 'user');
                // Add spacer after the last message of a turn before a role switch
                const isTurnEnd =
                    !!next && next.role !== msg.role;

                return (
                    <AgentMessageBubble
                        key={msg.id}
                        message={msg}
                        showAnchor={showAnchor}
                        isTurnEnd={isTurnEnd}
                        statusText={msg.isStreaming ? statusText : undefined}
                    />
                );
            })}
            <div ref={bottomRef} />
        </Box>
    );
};

export default QuickHelpMessages;
